import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AuthService } from '@auth0/auth0-angular';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import { IUserAuth } from 'src/app/library/interfaces/user/user';
import { EnvironmentService } from 'src/app/library/services/core/environment.service';
import { IAuthAccessToken } from '../interfaces/auth-access-token';

@Injectable({
    providedIn: 'root'
})
export class UserAuthService {
    private _localStorageUser = 'Auth0User';

    private _user = new BehaviorSubject<IUserAuth | undefined>(undefined);
    private _accessToken = new BehaviorSubject<string | undefined>(undefined);
    private _permissions = new BehaviorSubject<string[]>([]);
    private _loadingAuth = new BehaviorSubject<boolean>(true);

    constructor(
        private router: Router,
        private toastrService: ToastrService,
        private environmentService: EnvironmentService,
        private authService: AuthService
    ) {}

    public getDecodedAccessToken(token: string): IAuthAccessToken {
        const helper = new JwtHelperService();
        const decodedToken: IAuthAccessToken = helper.decodeToken(token);

        return decodedToken;
    }

    public async loadUser(): Promise<void> {
        const userJson = localStorage.getItem(this._localStorageUser);

        let user: IUserAuth;

        if (!userJson) {
            user = await new Promise<IUserAuth>(resolve => {
                const subscription = this.authService.user$.subscribe(data => {
                    resolve(data as IUserAuth);
                    subscription.unsubscribe(); // Unsubscribe once the value is emitted
                });
            });
        } else {
            user = JSON.parse(userJson);
        }

        this.accessToken = await this.authService
            .getAccessTokenSilently()
            .toPromise();
        this.user = user;
    }

    public hasPermission(perm: string): boolean {
        const permissions = this._permissions.value;
        return permissions.indexOf(perm) > -1;
    }

    public hasPermissions(perms: string[]): boolean {
        const permissions = this._permissions.value;
        return perms.some(p => {
            return permissions.indexOf(p) > -1;
        });
    }

    public hasAllPermissions(perms: string[]): boolean {
        const permissions = this._permissions.value;
        return perms.every(p => {
            return permissions.indexOf(p) > -1;
        });
    }

    public handleAccessCheckNotification(perms: string[]): boolean {
        const hasAccess = this.hasPermissions(perms);

        if (!hasAccess) {
            this.toastrService.error(
                'You do not have permission to perform this action',
                'Unauthorized'
            );
        }

        return hasAccess;
    }

    public handlePermissionCheck(perm: string): void {
        const hasPermission: boolean = this.hasPermission(perm);
        if (!hasPermission) {
            this.navigateToUnauthorised();
        }
    }

    public login(loginRedirect?: string): Observable<void> {
        this.user = undefined;
        this.accessToken = undefined;
        return this.authService.loginWithRedirect({
            authorizationParams: {
                redirect_uri:
                    this.environmentService.environmentConfig.auth0.callbackUrl
            },
            appState: { target: loginRedirect }
        });
    }
    public logout(): void {
        this.user = undefined;
        this.accessToken = undefined;
        this.authService.logout({
            logoutParams: {
                returnTo:
                    this.environmentService.environmentConfig.auth0
                        .logoutRedirectUrl
            },
            clientId: this.environmentService.environmentConfig.auth0.clientId
        });
    }

    public navigateToUnauthorised(): void {
        this.router.navigateByUrl('/auth/unauthorised');
    }

    set user(user: IUserAuth | undefined) {
        if (user) {
            localStorage.setItem(this._localStorageUser, JSON.stringify(user));
        } else {
            localStorage.removeItem(this._localStorageUser);
        }

        this._user.next(user);
    }

    get user$(): Observable<IUserAuth | undefined> {
        return this._user.asObservable();
    }

    get user(): IUserAuth | undefined {
        return this._user.value;
    }

    set accessToken(token: string | undefined) {
        if (token) {
            const tokenObj = this.getDecodedAccessToken(token);
            this._accessToken.next(token);
            this._permissions.next(tokenObj.permissions);
        }
    }
    get accessToken(): string | undefined {
        return this._accessToken.value;
    }
    get accessToken$(): Observable<string | undefined> {
        return this._accessToken.asObservable();
    }

    get permissions(): string[] {
        return this._permissions.value;
    }

    set loadingAuth(loading: boolean) {
        this._loadingAuth.next(loading);
    }

    get loadingAuth$(): Observable<boolean> {
        return this._loadingAuth.asObservable();
    }
}
